import React, { useState, useEffect } from 'react';
import {
  Wallet, Plus, Edit2, Trash2, Smartphone,
  TrendingUp, TrendingDown,
  ArrowUpRight, ArrowDownRight, ChevronLeft,
  Check, X, Star, Archive, Loader2, Calendar,
  Landmark, PiggyBank, ShoppingBag, ArrowLeftRight,
  User, Search, Filter
} from 'lucide-react';

import api from '../../services/api';
import { Toast } from '../ui/Toast';
import ConfirmDialog from '../ConfirmDialog';
import { useLoader } from '../../contexts/LoaderContext';
import { useParamsCompta } from '../../contexts/ParametresComptaContext';
import DemandesApprobationSection from './DemandesApprobationSection';

// ============================================================================
// TRÉSORERIE MODULE — Dashboard caisses + Mouvements
// ============================================================================

const TYPES_CAISSE = [
  { value: 'especes', label: 'Espèces', icon: Wallet, description: 'Caisse physique' },
  { value: 'banque', label: 'Compte bancaire', icon: Landmark, description: 'Compte en banque' },
  { value: 'mobile_money', label: 'Mobile Money', icon: Smartphone, description: 'Orange Money, MTN, etc.' },
  { value: 'menues_depenses', label: 'Menues dépenses', icon: ShoppingBag, description: 'Petite caisse' },
  { value: 'autre', label: 'Autre', icon: PiggyBank, description: 'Autre type' },
];

const COULEURS_CAISSE = [
  '#448696', '#448696', '#10b981', '#6366f1', '#f59e0b',
  '#ef4444', '#8b5cf6', '#ec4899', '#0ea5e9', '#14b8a6',
  '#f97316', '#78716c',
];

const TresorerieModule = () => {
  // ============================================================================
  // ÉTATS
  // ============================================================================
  const [currentView, setCurrentView] = useState('dashboard');

  // Dashboard
  const [caisses, setCaisses] = useState([]);
  const [totaux, setTotaux] = useState({ solde_total: 0, nb_caisses_actives: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  // Modal caisse
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingCaisse, setEditingCaisse] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    nom: '', description: '', type_caisse: 'especes',
    solde_initial: '', couleur: '#448696', est_principale: false,
  });

  // Archivage
  const [confirmDialogConfig, setConfirmDialogConfig] = useState(null);

  // Vue détail
  const [selectedCaisse, setSelectedCaisse] = useState(null);
  const [mouvements, setMouvements] = useState([]);
  const [totauxMois, setTotauxMois] = useState({ credits: 0, debits: 0 });
  const [loadingMouvements, setLoadingMouvements] = useState(false);
  const [categories, setCategories] = useState([]);

  // Modal mouvement
  const [showMouvementModal, setShowMouvementModal] = useState(false);
  const [mouvementForm, setMouvementForm] = useState({
    type_mouvement: 'credit', montant: '', categorie: '',
    description: '', reference: '',
    date_mouvement: new Date().toISOString().split('T')[0],
  });
  const [isSavingMouvement, setIsSavingMouvement] = useState(false);

  // Suppression mouvement
  const [confirmDeleteMouvement, setConfirmDeleteMouvement] = useState(null);

  // Transfert inter-caisses
  const [showTransfertModal, setShowTransfertModal] = useState(false);
  const [transfertForm, setTransfertForm] = useState({
    caisse_source_id: '', caisse_destination_id: '',
    montant: '', motif: '',
    date_transfert: new Date().toISOString().split('T')[0],
  });
  const [isSavingTransfert, setIsSavingTransfert] = useState(false);

  const { showLoader, hideLoader } = useLoader();
  const { formatMontant, symbole } = useParamsCompta();

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // ============================================================================
  // CHARGEMENT
  // ============================================================================
  useEffect(() => {
    loadCaisses();
    loadCategories();
  }, []);

  const loadCaisses = async () => {
    showLoader();
    setIsLoading(true);
    try {
      const response = await api.tresorerie.getCaisses();
      if (response.success) {
        setCaisses(response.data);
        setTotaux(response.totaux || { solde_total: 0, nb_caisses_actives: 0 });
        if (selectedCaisse) {
          const updated = response.data.find(c => c.id === selectedCaisse.id);
          if (updated) setSelectedCaisse(updated);
        }
      } else {
        showToast(response.message || 'Erreur chargement', 'error');
      }
    } catch (error) {
      console.error('Erreur chargement caisses:', error);
      showToast('Erreur lors du chargement des caisses', 'error');
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.tresorerie.getCategories();
      if (response.success) setCategories(response.data);
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
    }
  };

  const loadMouvements = async (caisseId) => {
    setLoadingMouvements(true);
    try {
      const response = await api.tresorerie.getMouvements(caisseId, { limit: 50 });
      if (response.success) {
        setMouvements(response.data);
        setTotauxMois(response.totaux_mois || { credits: 0, debits: 0 });
      }
    } catch (error) {
      console.error('Erreur chargement mouvements:', error);
    } finally {
      setLoadingMouvements(false);
    }
  };

  // ============================================================================
  // NAVIGATION
  // ============================================================================
  const openCaisseDetail = (caisse) => {
    setSelectedCaisse(caisse);
    setCurrentView('detail');
    loadMouvements(caisse.id);
  };

  const backToDashboard = () => {
    setCurrentView('dashboard');
    setSelectedCaisse(null);
    setMouvements([]);
    loadCaisses();
  };

  // ============================================================================
  // CRUD CAISSES
  // ============================================================================
  const openCreateModal = () => {
    setModalMode('create');
    setEditingCaisse(null);
    setFormData({ nom: '', description: '', type_caisse: 'especes', solde_initial: '', couleur: '#448696', est_principale: false });
    setShowModal(true);
  };

  const openEditModal = (caisse) => {
    setModalMode('edit');
    setEditingCaisse(caisse);
    setFormData({ nom: caisse.nom, description: caisse.description || '', type_caisse: caisse.type_caisse, solde_initial: '', couleur: caisse.couleur || '#448696', est_principale: Boolean(caisse.est_principale) });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.nom.trim()) { showToast('Le nom de la caisse est obligatoire', 'error'); return; }
    setIsSaving(true);
    try {
      let response;
      if (modalMode === 'create') {
        response = await api.tresorerie.creerCaisse({ ...formData, solde_initial: formData.solde_initial ? parseFloat(formData.solde_initial) : 0 });
      } else {
        response = await api.tresorerie.modifierCaisse(editingCaisse.id, formData);
      }
      if (response.success) {
        showToast(response.message || (modalMode === 'create' ? 'Caisse créée' : 'Caisse modifiée'), 'success');
        setShowModal(false);
        await loadCaisses();
      } else {
        showToast(response.message || 'Erreur', 'error');
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      showToast('Erreur lors de la sauvegarde', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async (caisse) => {
    try {
      const response = await api.tresorerie.archiverCaisse(caisse.id);
      if (response.success) { showToast('Caisse archivée', 'success'); await loadCaisses(); return true; }
      else { showToast(response.message || 'Erreur', 'error'); return false; }
    } catch (error) {
      console.error('Erreur archivage:', error);
      showToast('Erreur lors de l\'archivage', 'error');
      return false;
    }
  };

  // ============================================================================
  // CRUD MOUVEMENTS
  // ============================================================================
  const openMouvementModal = (type = 'credit') => {
    setMouvementForm({ type_mouvement: type, montant: '', categorie: '', description: '', reference: '', date_mouvement: new Date().toISOString().split('T')[0] });
    setShowMouvementModal(true);
  };

  const handleSaveMouvement = async () => {
    if (!mouvementForm.montant || parseFloat(mouvementForm.montant) <= 0) {
      showToast('Le montant doit être supérieur à 0', 'error');
      return;
    }
    setIsSavingMouvement(true);
    try {
      const response = await api.tresorerie.creerMouvement({ caisse_id: selectedCaisse.id, ...mouvementForm, montant: parseFloat(mouvementForm.montant) });
      if (response.success) {
        if (response.approbation_requise) {
          showToast('Dépense soumise à approbation, en attente de validation', 'info');
        } else {
          showToast(response.message || 'Mouvement enregistré', 'success');
        }
        setShowMouvementModal(false);
        await loadMouvements(selectedCaisse.id);
        await loadCaisses();
      } else {
        showToast(response.message || 'Erreur', 'error');
      }
   } catch (error) {
      console.error('Erreur création mouvement:', error);
      const msg = error.response?.data?.message || error.message || 'Erreur lors de l\'enregistrement';
      showToast(msg, 'error');
    } finally {
      setIsSavingMouvement(false);
    }
  };

  const handleDeleteMouvement = async (mouvement) => {
    try {
      const response = await api.tresorerie.supprimerMouvement(mouvement.id);
      if (response.success) {
        showToast('Mouvement supprimé', 'success');
        await loadMouvements(selectedCaisse.id);
        await loadCaisses();
      } else {
        showToast(response.message || 'Erreur', 'error');
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      const msg = error.response?.data?.message || error.message || 'Erreur lors de la suppression';
      showToast(msg, 'error');
    }
  };

  // ============================================================================
  // TRANSFERTS
  // ============================================================================
  const openTransfertModal = () => {
    setTransfertForm({
      caisse_source_id: selectedCaisse ? selectedCaisse.id.toString() : '',
      caisse_destination_id: '',
      montant: '', motif: '',
      date_transfert: new Date().toISOString().split('T')[0],
    });
    setShowTransfertModal(true);
  };

  const handleSaveTransfert = async () => {
    if (!transfertForm.caisse_source_id || !transfertForm.caisse_destination_id) {
      showToast('Sélectionnez les deux caisses', 'error');
      return;
    }
    if (transfertForm.caisse_source_id === transfertForm.caisse_destination_id) {
      showToast('Les caisses source et destination doivent être différentes', 'error');
      return;
    }
    if (!transfertForm.montant || parseFloat(transfertForm.montant) <= 0) {
      showToast('Le montant doit être supérieur à 0', 'error');
      return;
    }
    setIsSavingTransfert(true);
    try {
     const response = await api.tresorerie.creerTransfert({
        ...transfertForm,
        caisse_source_id: parseInt(transfertForm.caisse_source_id),
        caisse_destination_id: parseInt(transfertForm.caisse_destination_id),
        montant: parseFloat(transfertForm.montant),
      });
      if (response.success) {
        if (response.approbation_requise) {
          showToast('Transfert soumis à approbation, en attente de validation', 'info');
        } else {
          showToast(response.message || 'Transfert effectué', 'success');
        }
        setShowTransfertModal(false);
        await loadCaisses();
        if (selectedCaisse) await loadMouvements(selectedCaisse.id);
      } else {
        showToast(response.message || 'Erreur', 'error');
      }

    } catch (error) {
      console.error('Erreur transfert:', error);
      const msg = error.response?.data?.message || error.message || 'Erreur lors du transfert';
      showToast(msg, 'error');
    } finally {
      setIsSavingTransfert(false);
    }
  };

  // ============================================================================
  // HELPERS
  // ============================================================================
  const getTypeConfig = (type) => TYPES_CAISSE.find(t => t.value === type) || TYPES_CAISSE[0];
 

  // ============================================================================
  // VUE DASHBOARD
  // ============================================================================
  const renderDashboard = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5 transition-all hover:shadow-md" style={{ borderLeft: '3px solid #448696', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#f0f9fb' }}><Wallet size={20} style={{ color: '#448696' }} /></div>
          </div>
          <div className="text-2xl font-bold tracking-tight" style={{ color: '#1a1a2e' }}>
            {isLoading ? <div className="h-7 w-32 bg-gray-100 rounded-lg animate-pulse" /> : `${formatMontant(totaux.solde_total)}`}
          </div>
          <div className="text-xs font-semibold mt-1.5 text-gray-500">Solde global</div>
        </div>
        <div className="bg-white rounded-2xl p-5 transition-all hover:shadow-md" style={{ borderLeft: '3px solid #fed774', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#fffbeb' }}><PiggyBank size={20} style={{ color: '#d4a017' }} /></div>
          </div>
          <div className="text-2xl font-bold tracking-tight" style={{ color: '#1a1a2e' }}>
            {isLoading ? <div className="h-7 w-12 bg-gray-100 rounded-lg animate-pulse" /> : totaux.nb_caisses_actives}
          </div>
          <div className="text-xs font-semibold mt-1.5 text-gray-500">Caisses actives</div>
        </div>
        <div onClick={openCreateModal} className="bg-white rounded-2xl p-5 transition-all hover:shadow-md cursor-pointer group" style={{ border: '2px dashed #d1d5db' }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = '#448696'} onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}>
          <div className="flex items-center justify-center h-full gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#f0f9fb' }}><Plus size={22} style={{ color: '#448696' }} /></div>
            <div><div className="text-sm font-bold text-gray-700 group-hover:text-[#448696] transition-colors">Nouvelle caisse</div><div className="text-xs text-gray-400">Ajouter un portefeuille</div></div>
          </div>
        </div>
        {caisses.filter(c => c.statut === 'active').length >= 2 && (
          <div onClick={openTransfertModal} className="bg-white rounded-2xl p-5 transition-all hover:shadow-md cursor-pointer group" style={{ border: '2px dashed #93c5fd' }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#3b82f6'} onMouseLeave={(e) => e.currentTarget.style.borderColor = '#93c5fd'}>
            <div className="flex items-center justify-center h-full gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#eff6ff' }}><ArrowLeftRight size={22} style={{ color: '#3b82f6' }} /></div>
              <div><div className="text-sm font-bold text-gray-700 group-hover:text-blue-600 transition-colors">Transfert</div><div className="text-xs text-gray-400">Entre caisses</div></div>
            </div>
          </div>
        )}
      </div>

      <DemandesApprobationSection
        typeDocument="depense"
        titre="Dépenses en attente d'approbation"
        onRefresh={() => { loadCaisses(); if (selectedCaisse) loadMouvements(selectedCaisse.id); }}
      />
      <DemandesApprobationSection
        typeDocument="transfert"
        titre="Transferts en attente d'approbation"
        onRefresh={() => { loadCaisses(); if (selectedCaisse) loadMouvements(selectedCaisse.id); }}
      />

      <div className="mb-4">
        <div className="flex items-center gap-2.5 mb-5">
          <Wallet size={15} style={{ color: '#448696' }} />
          <h2 className="text-xs font-bold uppercase tracking-widest" style={{ color: '#448696' }}>Vos caisses</h2>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {isLoading ? null : caisses.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
            <Wallet className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-bold text-gray-700 mb-2">Aucune caisse créée</h3>
            <button onClick={openCreateModal} className="px-6 py-3 text-white rounded-xl font-semibold text-sm shadow-lg" style={{ backgroundColor: '#448696' }}>
              <Plus className="w-4 h-4 inline mr-2" />Créer ma première caisse
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {caisses.filter(c => c.statut === 'active').map((caisse) => {
              const typeConfig = getTypeConfig(caisse.type_caisse);
              const TypeIcon = typeConfig.icon;
              const solde = parseFloat(caisse.solde_actuel || 0);
              const isNegative = solde < 0;
              return (
                <div key={caisse.id} onClick={() => openCaisseDetail(caisse)}
                  className="bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-lg group cursor-pointer"
                  style={{ border: `1.5px solid ${caisse.couleur}20`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = caisse.couleur + '50'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = caisse.couleur + '20'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                  <div className="h-1.5" style={{ backgroundColor: caisse.couleur }} />
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: caisse.couleur + '12' }}>
                        <TypeIcon size={22} style={{ color: caisse.couleur }} />
                      </div>
                      <div className="flex items-center gap-1.5">
                        {caisse.est_principale === 1 && (
                          <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
                            <Star size={12} style={{ color: '#d4a017' }} fill="#d4a017" />
                          </div>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); openEditModal(caisse); }}
                          className="w-7 h-7 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-gray-100">
                          <Edit2 size={14} className="text-gray-400" />
                        </button>
                        {!caisse.est_principale && (
                          <button onClick={(e) => { e.stopPropagation(); setConfirmDialogConfig({ caisse, title: 'Archiver cette caisse', message: `Voulez-vous archiver "${caisse.nom}" ?` }); }}
                            className="w-7 h-7 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-red-50">
                            <Archive size={14} className="text-red-400" />
                          </button>
                        )}
                      </div>
                    </div>
                    <h3 className="text-[15px] font-bold mb-0.5" style={{ color: '#1a1a2e' }}>{caisse.nom}</h3>
                    <p className="text-xs font-semibold text-gray-400 mb-4">{typeConfig.label}</p>
                    <div className="p-3 rounded-xl" style={{ backgroundColor: isNegative ? '#fef2f2' : caisse.couleur + '08', border: `1px solid ${isNegative ? '#fecaca' : caisse.couleur + '15'}` }}>
                      <div className="text-xs font-semibold text-gray-500 mb-1">Solde actuel</div>
                      <div className="text-xl font-bold" style={{ color: isNegative ? '#ef4444' : caisse.couleur }}>{formatMontant(solde)}</div>
                    </div>
                    {caisse.nb_mouvements > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100 text-[11px] text-gray-400 font-medium">
                        {caisse.nb_mouvements} mouvement{caisse.nb_mouvements > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  // ============================================================================
  // VUE DÉTAIL CAISSE
  // ============================================================================
  const renderDetail = () => {
    if (!selectedCaisse) return null;
    const typeConfig = getTypeConfig(selectedCaisse.type_caisse);
    const TypeIcon = typeConfig.icon;
    const solde = parseFloat(selectedCaisse.solde_actuel || 0);

    return (
      <>
        <button onClick={backToDashboard} className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors mb-5">
          <ChevronLeft size={18} /> Retour aux caisses
        </button>

        {/* Header caisse */}
        <div className="bg-white rounded-2xl p-6 mb-6" style={{ borderLeft: `4px solid ${selectedCaisse.couleur}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: selectedCaisse.couleur + '12' }}>
                <TypeIcon size={28} style={{ color: selectedCaisse.couleur }} />
              </div>
              <div>
                <h2 className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{selectedCaisse.nom}</h2>
                <p className="text-sm font-medium text-gray-500">{typeConfig.label}{selectedCaisse.description ? ` • ${selectedCaisse.description}` : ''}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs font-semibold text-gray-500 mb-1">Solde actuel</div>
              <div className="text-3xl font-bold" style={{ color: solde < 0 ? '#ef4444' : selectedCaisse.couleur }}>{formatMontant(solde)}</div>
            </div>
          </div>
        </div>

        {/* KPIs + boutons */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-4" style={{ borderLeft: '3px solid #10b981', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center gap-2 mb-2"><ArrowDownRight size={16} className="text-green-600" /><span className="text-xs font-semibold text-gray-500">Entrées du mois</span></div>
            <div className="text-lg font-bold text-green-600">{formatMontant(totauxMois.credits)}</div>
          </div>
          <div className="bg-white rounded-2xl p-4" style={{ borderLeft: '3px solid #ef4444', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center gap-2 mb-2"><ArrowUpRight size={16} className="text-red-600" /><span className="text-xs font-semibold text-gray-500">Sorties du mois</span></div>
            <div className="text-lg font-bold text-red-600">{formatMontant(totauxMois.debits)}</div>
          </div>
          <button onClick={() => openMouvementModal('credit')} className="rounded-2xl p-4 text-left transition-all hover:shadow-md" style={{ backgroundColor: '#d4f4dd', border: '2px solid #86efac' }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
            <div className="flex items-center gap-2 mb-1"><Plus size={18} className="text-green-700" /><span className="text-sm font-bold text-green-700">Nouvelle entrée</span></div>
            <span className="text-xs font-medium text-green-600">Enregistrer un crédit</span>
          </button>
          <button onClick={() => openMouvementModal('debit')} className="rounded-2xl p-4 text-left transition-all hover:shadow-md" style={{ backgroundColor: '#fee2e2', border: '2px solid #fca5a5' }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
            <div className="flex items-center gap-2 mb-1"><ArrowUpRight size={18} className="text-red-700" /><span className="text-sm font-bold text-red-700">Nouvelle sortie</span></div>
            <span className="text-xs font-medium text-red-600">Enregistrer un débit</span>
          </button>
          <button onClick={openTransfertModal} className="rounded-2xl p-4 text-left transition-all hover:shadow-md" style={{ backgroundColor: '#eff6ff', border: '2px solid #93c5fd' }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
            <div className="flex items-center gap-2 mb-1"><ArrowLeftRight size={18} className="text-blue-700" /><span className="text-sm font-bold text-blue-700">Transfert</span></div>
            <span className="text-xs font-medium text-blue-600">Entre caisses</span>
          </button>
        </div>

        {/* Liste mouvements */}
        <div className="bg-white rounded-2xl border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ArrowLeftRight size={18} style={{ color: selectedCaisse.couleur }} />
              <h3 className="text-sm font-bold text-gray-800">Mouvements récents</h3>
              <span className="text-xs font-medium text-gray-400 ml-2">{mouvements.length} mouvement{mouvements.length > 1 ? 's' : ''}</span>
            </div>
          </div>

          {loadingMouvements ? (
            <div className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-300" /></div>
          ) : mouvements.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              <ArrowLeftRight className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm font-medium">Aucun mouvement enregistré</p>
              <p className="text-xs mt-1">Utilisez les boutons ci-dessus pour enregistrer une entrée ou sortie</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {mouvements.map((mv) => {
                const isCredit = mv.type_mouvement === 'credit';
                return (
                  <div key={mv.id} className="px-6 py-4 hover:bg-gray-50 transition-colors group">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCredit ? 'bg-green-50' : 'bg-red-50'}`}>
                        {isCredit ? <ArrowDownRight size={20} className="text-green-600" /> : <ArrowUpRight size={20} className="text-red-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-bold text-gray-900">{mv.description || mv.categorie || (isCredit ? 'Entrée' : 'Sortie')}</span>
                          {mv.categorie && mv.description && (
                            <span className="text-xs px-2 py-0.5 rounded-md font-semibold" style={{ backgroundColor: isCredit ? '#d4f4dd' : '#fee2e2', color: isCredit ? '#166534' : '#991b1b' }}>
                              {mv.categorie}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-400 font-medium">
                          <span>{new Date(mv.date_mouvement).toLocaleDateString('fr-FR')}</span>
                          {mv.reference && <span>Réf: {mv.reference}</span>}
                          {mv.created_by_name && <span className="flex items-center gap-1"><User size={11} />{mv.created_by_name}</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-bold ${isCredit ? 'text-green-600' : 'text-red-600'}`}>{isCredit ? '+' : '-'}{formatMontant(mv.montant)}</div>
                        <div className="text-[11px] text-gray-400 font-medium">Solde: {formatMontant(mv.solde_apres)}</div>
                      </div>
                      {mv.source_type === 'manuel' && (
                        <button onClick={() => setConfirmDeleteMouvement(mv)}
                          className="w-8 h-8 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-red-50">
                          <Trash2 size={14} className="text-red-400" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </>
    );
  };

  // ============================================================================
  // RENDU PRINCIPAL
  // ============================================================================
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>
      {currentView === 'dashboard' ? renderDashboard() : renderDetail()}

      {/* MODAL CAISSE */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
              {modalMode === 'create' ? <Plus size={24} className="text-white" /> : <Edit2 size={24} className="text-white" />}
              <h3 className="text-xl font-bold text-white">{modalMode === 'create' ? 'Nouvelle caisse' : 'Modifier la caisse'}</h3>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Nom de la caisse *</label>
              <input type="text" value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} placeholder="Ex: Caisse principale..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="2"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Type de caisse *</label>
              <div className="grid grid-cols-2 gap-2">
                {TYPES_CAISSE.map(type => {
                  const Icon = type.icon; const isSelected = formData.type_caisse === type.value;
                  return (
                    <button key={type.value} onClick={() => setFormData({ ...formData, type_caisse: type.value })}
                      className={`p-3 rounded-xl text-left transition-all border-2 ${isSelected ? 'shadow-sm' : 'border-gray-200 hover:bg-gray-50'}`}
                      style={isSelected ? { borderColor: formData.couleur, backgroundColor: formData.couleur + '08' } : {}}>
                      <div className="flex items-center gap-2"><Icon size={16} style={{ color: isSelected ? formData.couleur : '#9ca3af' }} />
                        <span className={`text-xs font-semibold ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>{type.label}</span></div>
                    </button>
                  );
                })}
              </div>
            </div>
            {modalMode === 'create' && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Solde initial ({symbole})</label>
                <input type="number" value={formData.solde_initial} onChange={(e) => setFormData({ ...formData, solde_initial: e.target.value })} placeholder="0"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                  onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
              </div>
            )}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Couleur</label>
              <div className="flex flex-wrap gap-2">
                {COULEURS_CAISSE.map(c => (
                  <button key={c} onClick={() => setFormData({ ...formData, couleur: c })} className="w-8 h-8 rounded-lg transition-all"
                    style={{ backgroundColor: c, border: formData.couleur === c ? '3px solid #1a1a2e' : '2px solid transparent', transform: formData.couleur === c ? 'scale(1.15)' : 'scale(1)' }} />
                ))}
              </div>
            </div>
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all"
                style={{ borderColor: formData.est_principale ? '#d4a017' : '#e5e5e5', backgroundColor: formData.est_principale ? '#fffbeb' : 'transparent' }}>
                <input type="checkbox" checked={formData.est_principale} onChange={(e) => setFormData({ ...formData, est_principale: e.target.checked })} className="sr-only" />
                <div className="w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all"
                  style={{ borderColor: formData.est_principale ? '#d4a017' : '#d1d5db', backgroundColor: formData.est_principale ? '#d4a017' : 'transparent' }}>
                  {formData.est_principale && <Check size={12} className="text-white" />}
                </div>
                <div><div className="text-sm font-semibold text-gray-700">Caisse principale</div><div className="text-xs text-gray-400">Sera mise en avant</div></div>
                <Star size={16} className="ml-auto" style={{ color: formData.est_principale ? '#d4a017' : '#d1d5db' }} />
              </label>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">Annuler</button>
              <button onClick={handleSave} disabled={isSaving} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-50" style={{ backgroundColor: '#448696' }}
                onMouseEnter={(e) => !isSaving && (e.currentTarget.style.backgroundColor = '#4a8391')} onMouseLeave={(e) => !isSaving && (e.currentTarget.style.backgroundColor = '#448696')}>
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (modalMode === 'create' ? 'Créer' : 'Enregistrer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL MOUVEMENT */}
      {showMouvementModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3"
              style={{ backgroundColor: mouvementForm.type_mouvement === 'credit' ? '#10b981' : '#ef4444' }}>
              {mouvementForm.type_mouvement === 'credit' ? <ArrowDownRight size={24} className="text-white" /> : <ArrowUpRight size={24} className="text-white" />}
              <h3 className="text-xl font-bold text-white">{mouvementForm.type_mouvement === 'credit' ? 'Nouvelle entrée' : 'Nouvelle sortie'}</h3>
            </div>
            <div className="flex gap-2 mb-5">
              <button onClick={() => setMouvementForm({ ...mouvementForm, type_mouvement: 'credit' })}
                className={`flex-1 p-3 rounded-xl font-semibold text-sm transition-all border-2 ${mouvementForm.type_mouvement === 'credit' ? 'bg-green-50 border-green-500 text-green-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                Entrée (Crédit)
              </button>
              <button onClick={() => setMouvementForm({ ...mouvementForm, type_mouvement: 'debit' })}
                className={`flex-1 p-3 rounded-xl font-semibold text-sm transition-all border-2 ${mouvementForm.type_mouvement === 'debit' ? 'bg-red-50 border-red-500 text-red-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                Sortie (Débit)
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Montant ({symbole}) *</label>
              <input type="number" value={mouvementForm.montant} onChange={(e) => setMouvementForm({ ...mouvementForm, montant: e.target.value })} placeholder="0"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-lg focus:outline-none transition-all"
                onFocus={(e) => { const c = mouvementForm.type_mouvement === 'credit' ? '#10b981' : '#ef4444'; e.target.style.borderColor = c; e.target.style.boxShadow = `0 0 0 3px ${c}15`; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Catégorie</label>
              <select value={mouvementForm.categorie} onChange={(e) => setMouvementForm({ ...mouvementForm, categorie: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}>
                <option value="">Sélectionner...</option>
                {categories.filter(c => c.type_mouvement === mouvementForm.type_mouvement || c.type_mouvement === 'les_deux').map(c => (
                  <option key={c.id} value={c.nom}>{c.nom}</option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
              <textarea value={mouvementForm.description} onChange={(e) => setMouvementForm({ ...mouvementForm, description: e.target.value })} rows="2" placeholder="Détail du mouvement..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Référence</label>
                <input type="text" value={mouvementForm.reference} onChange={(e) => setMouvementForm({ ...mouvementForm, reference: e.target.value })} placeholder="N° facture..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                  onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
                <input type="date" value={mouvementForm.date_mouvement} onChange={(e) => setMouvementForm({ ...mouvementForm, date_mouvement: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                  onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowMouvementModal(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">Annuler</button>
              <button onClick={handleSaveMouvement} disabled={isSavingMouvement}
                className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                style={{ backgroundColor: mouvementForm.type_mouvement === 'credit' ? '#10b981' : '#ef4444' }}>
                {isSavingMouvement ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL TRANSFERT */}
      {showTransfertModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#3b82f6' }}>
              <ArrowLeftRight size={24} className="text-white" />
              <h3 className="text-xl font-bold text-white">Transfert inter-caisses</h3>
            </div>

            {/* Caisse source */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Caisse source *</label>
              <select value={transfertForm.caisse_source_id} onChange={(e) => setTransfertForm({ ...transfertForm, caisse_source_id: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}>
                <option value="">Sélectionner la source...</option>
                {caisses.filter(c => c.statut === 'active').map(c => (
                  <option key={c.id} value={c.id}>{c.nom} ({formatMontant(c.solde_actuel)})</option>
                ))}
              </select>
            </div>

            {/* Flèche visuelle */}
            <div className="flex justify-center mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                <ArrowDownRight size={20} className="text-blue-600" />
              </div>
            </div>

            {/* Caisse destination */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Caisse destination *</label>
              <select value={transfertForm.caisse_destination_id} onChange={(e) => setTransfertForm({ ...transfertForm, caisse_destination_id: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}>
                <option value="">Sélectionner la destination...</option>
                {caisses.filter(c => c.statut === 'active' && c.id.toString() !== transfertForm.caisse_source_id).map(c => (
                  <option key={c.id} value={c.id}>{c.nom} ({formatMontant(c.solde_actuel)})</option>
                ))}
              </select>
            </div>

            {/* Montant */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Montant ({symbole}) *</label>
              <input type="number" value={transfertForm.montant} onChange={(e) => setTransfertForm({ ...transfertForm, montant: e.target.value })} placeholder="0"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-lg focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
              {transfertForm.caisse_source_id && (
                <div className="mt-1 text-xs font-medium text-gray-500">
                  Disponible : {formatMontant(caisses.find(c => c.id.toString() === transfertForm.caisse_source_id)?.solde_actuel || 0)}
                </div>
              )}
            </div>

            {/* Motif + Date */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Motif</label>
              <input type="text" value={transfertForm.motif} onChange={(e) => setTransfertForm({ ...transfertForm, motif: e.target.value })}
                placeholder="Raison du transfert..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
              <input type="date" value={transfertForm.date_transfert} onChange={(e) => setTransfertForm({ ...transfertForm, date_transfert: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowTransfertModal(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">Annuler</button>
              <button onClick={handleSaveTransfert} disabled={isSavingTransfert}
                className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                style={{ backgroundColor: '#3b82f6' }}
                onMouseEnter={(e) => !isSavingTransfert && (e.currentTarget.style.backgroundColor = '#2563eb')}
                onMouseLeave={(e) => !isSavingTransfert && (e.currentTarget.style.backgroundColor = '#3b82f6')}>
                {isSavingTransfert ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Transférer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ConfirmDialog archivage */}
      {confirmDialogConfig && (
        <ConfirmDialog isOpen={true} title={confirmDialogConfig.title} description={confirmDialogConfig.message}
          type="warning" confirmText="Archiver" cancelText="Annuler"
          onClose={() => setConfirmDialogConfig(null)}
          onConfirm={async () => { await handleArchive(confirmDialogConfig.caisse); setConfirmDialogConfig(null); }} />
      )}

      {/* ConfirmDialog suppression mouvement */}
      {confirmDeleteMouvement && (
        <ConfirmDialog isOpen={true} title="Supprimer ce mouvement"
          description={`Supprimer ${confirmDeleteMouvement.type_mouvement === 'credit' ? "l'entrée" : 'la sortie'} de ${formatMontant(confirmDeleteMouvement.montant)} ? Le solde sera recalculé.`}
          type="warning" confirmText="Supprimer" cancelText="Annuler"
          onClose={() => setConfirmDeleteMouvement(null)}
          onConfirm={async () => { await handleDeleteMouvement(confirmDeleteMouvement); setConfirmDeleteMouvement(null); }} />
      )}

      {/* Toast */}
      {toast.show && <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ show: false, message: '', type: '' })} />}
    </div>

    
  );
};

export default TresorerieModule;